import {
    CompanionPushEvent,
    CompanionPushPayload,
    LocationSyncPayload,
    LocationTrackerTrackingState,
    LocationTrackerUserConfig,
} from './location-tracker.wire';
import { createDefaultLocationTrackerUserConfigState, LocationTrackerUserConfigState } from './location-tracker.state';

/**
 * Wire config -> slice state. `null` coordinates (and a missing `PrivacyPerimeterLocation`
 * altogether, which the backend has been observed to omit) both become `undefined`.
 *
 * The `?? false` / `?? ''` fallbacks look redundant against the wire type, which declares all
 * three fields required. They are not: this is parsed JSON from a backend, so the type is a claim
 * rather than a guarantee, and `LocationTrackerModel` guarded the same three fields the same way.
 * Without them a partial payload would put `undefined` into fields the slice types as `boolean`
 * and `string`, and the form would bind to it.
 */
export function wireConfigToConfigState(wire: LocationTrackerUserConfig): LocationTrackerUserConfigState {
    return {
        shareLocation: wire.ShareLocation ?? false,
        privacyPerimeterLatitude: wire.PrivacyPerimeterLocation?.Latitude ?? undefined,
        privacyPerimeterLongitude: wire.PrivacyPerimeterLocation?.Longitude ?? undefined,
        state: wire.State ?? '',
        businessLine: wire.BusinessLine ?? '',
    };
}

/**
 * Slice state -> wire config. `undefined` coordinates become `null` (see `location-tracker.wire.ts`);
 * `state` and `businessLine` are trimmed here, where `LocationTrackerModel.getOutModel()` used to trim them.
 */
export function configStateToWireConfig(config: LocationTrackerUserConfigState): LocationTrackerUserConfig {
    return {
        ShareLocation: config.shareLocation,
        PrivacyPerimeterLocation: {
            Latitude: config.privacyPerimeterLatitude ?? null,
            Longitude: config.privacyPerimeterLongitude ?? null,
        },
        State: config.state.trim(),
        BusinessLine: config.businessLine.trim(),
    };
}

/**
 * User-store mirrored value (`unknown` — the user-store slice mirrors any JSON shape per key) ->
 * the location-tracker config state. Tolerant like `parseHydratedUserStoreState` in
 * `user-store.converters.ts`: a missing/`undefined` value, `null`, or anything not shaped like an
 * object falls back to the default config rather than throwing; a malformed field within an
 * object is still caught by `wireConfigToConfigState`'s own `?? false` / `?? ''` fallbacks.
 */
export function userStoreValueToConfigState(value: unknown): LocationTrackerUserConfigState {
    if (typeof value !== 'object' || value === null) {
        return createDefaultLocationTrackerUserConfigState();
    }
    return wireConfigToConfigState(value as LocationTrackerUserConfig);
}

/**
 * User-store mirrored value -> the location-tracker tracking-state timestamps. Tolerant the same
 * way as `userStoreValueToConfigState` above: a missing/`undefined`/non-object value, or a
 * wrong-typed field within it, falls back to `undefined` for that field rather than throwing —
 * modeled on `parseHydratedUserStoreState` in `user-store.converters.ts`.
 */
export function userStoreValueToTrackingState(value: unknown): LocationTrackerTrackingState {
    const candidate =
        typeof value === 'object' && value !== null ? (value as Partial<LocationTrackerTrackingState>) : {};
    return {
        startOfWork: typeof candidate.startOfWork === 'string' ? candidate.startOfWork : undefined,
        endOfWork: typeof candidate.endOfWork === 'string' ? candidate.endOfWork : undefined,
    };
}

/**
 * `companionPushRequested`'s props -> the wire body posted to `ENDPOINTS.IosPushNotify`. Built
 * explicitly field-by-field (rather than spreading the props) so the action's NgRx `type` field
 * can never leak onto the wire.
 */
export function companionPushRequestToPayload(props: {
    event: CompanionPushEvent;
    occurredAt: string;
    startOfWork?: string;
}): CompanionPushPayload {
    return { event: props.event, occurredAt: props.occurredAt, startOfWork: props.startOfWork };
}

/**
 * Slice state -> the wire body posted to `ENDPOINTS.LocationSync`. `isTrackingAllowed` mirrors the
 * user's `shareLocation` consent: SENSE needs to be told explicitly when consent is withdrawn, not
 * just when it is granted.
 */
export function configStateToLocationSyncPayload(config: LocationTrackerUserConfigState): LocationSyncPayload {
    return { isTrackingAllowed: config.shareLocation };
}
